<script setup>
import { useAppStore } from '../store/app';
import { ref, defineEmits, onMounted } from 'vue';
import validateAddProduct from '../validations/validateAddProduct';
import { ACTIVE } from '../common/constants';

const props = defineProps({
  isNew: {
    type: Boolean,
    required: true,
  },
  product: {
    type: Object,
    required: false,
  },
});
const emit = defineEmits(['onClose']);

const store = useAppStore();

onMounted(() => {
  if (!props.isNew) {
    product.value = { ...props.product };
  }
});

const product = ref({
  name: '',
  description: '',
  freeTrialAvailable: false,
  daysFreeTrial: 0,
});

const errors = ref({});

const onSaveProduct = async (event) => {
  event.preventDefault();
  const { inputErrors, isValid } = validateAddProduct(product.value);

  // Check Validation
  if (!isValid) {
    errors.value = inputErrors;
    return;
  } else {
    errors.value = {}; // Reset errors
  }

  const newProduct = {
    name: product.value.name.trim(),
    description: product.value.description.trim(),
    status: ACTIVE,
    freeTrialAvailable: Number(product.value.freeTrialAvailable),
    daysFreeTrial: Number(product.value.daysFreeTrial),
    code: product.value.code.trim(),
  };

  if (!props.isNew) {
    newProduct.id = product.value.id;
  }

  const res = await store.createNewProduct({ newProduct });
  emit('onClose', res);
};

const showDialog = ref(true);
const onClose = () => {
  emit('onClose');
};
</script>

<template>
  <v-dialog persistent v-model="showDialog" width="500">
    <v-card>
      <v-card-title>
        <h4 class="text-center mb-2">{{ props.isNew ? 'New Product' : 'Edit Product' }}</h4>
      </v-card-title>
      <v-card-text>
        <v-form>
          <v-row>
            <v-col>
              <v-text-field
                v-model="product.name"
                label="Name"
                placeholder="Product Name"
                name="name"
                :error-messages="errors.name"
                variant="outlined"
                density="compact"
                persistent-placeholder>
              </v-text-field>
            </v-col>
            <v-col>
              <v-text-field
                v-model="product.code"
                label="Code"
                placeholder="Product Code"
                name="code"
                :error-messages="errors.code"
                variant="outlined"
                density="compact"
                persistent-placeholder>
              </v-text-field>
            </v-col>
          </v-row>
          <v-textarea
            v-model="product.description"
            label="Description"
            placeholder="Product Description"
            name="description"
            :error-messages="errors.description"
            variant="outlined"
            density="compact"
            persistent-placeholder>
          </v-textarea>
          <v-row>
            <v-col>
              <v-switch
                v-model="product.freeTrialAvailable"
                :label="`Free Trial: ${product.freeTrialAvailable ? 'Yes' : 'No'}`"
                :error-messages="errors.freeTrialAvailable"
                outlined
                :true-value="1"
                :false-value="0"
                :color="product.freeTrialAvailable ? 'success' : 'grey'"
                dense
                required>
              </v-switch>
            </v-col>
            <v-col>
              <v-text-field
                v-model="product.daysFreeTrial"
                label="Days of Free Trial"
                placeholder="Days of Free Trial"
                :error-messages="errors.daysFreeTrial"
                variant="outlined"
                density="compact"
                type="number"
                :disabled="!product.freeTrialAvailable"
                persistent-placeholder>
              </v-text-field>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" text @click="onClose">Cancel</v-btn>
        <v-btn class="text-white" width="100" color="#E2215A" variant="flat" @click="onSaveProduct"
          >Save</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style lang="scss" scoped></style>
